// frontend/src/pages/DatabaseConnect.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { databaseAPI } from '../services/api';
import NavBar from '../components/NavBar';

const DatabaseConnect = () => {
  const [formData, setFormData] = useState({
    type: 'postgresql',
    host: '',
    port: '5432',
    database: '',
    user: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const dbTypes = [
    { value: 'postgresql', label: 'PostgreSQL', defaultPort: '5432', icon: '🐘' },
    { value: 'mysql', label: 'MySQL', defaultPort: '3306', icon: '🐬' },
    { value: 'mssql', label: 'MS SQL Server', defaultPort: '1433', icon: '🗄️' },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Auto-fill port when database type changes
    if (name === 'type') {
      const selectedDb = dbTypes.find(db => db.value === value);
      setFormData(prev => ({
        ...prev,
        port: selectedDb.defaultPort,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const response = await databaseAPI.connect(formData.type, {
        host: formData.host,
        port: formData.port,
        user: formData.user,
        password: formData.password,
        database: formData.database,
        database_name: formData.database
      });
      
      if (response.database_id) {
        setSuccess(true);
        setTimeout(() => {
          navigate(`/chat/${response.database_id}`);
        }, 1500);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.error || 'Failed to connect to database');
    } finally {
      setLoading(false);
    }
  };

  const selectedDbType = dbTypes.find(db => db.value === formData.type);

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-950 via-purple-900 to-gray-950">
      <NavBar />
      
      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">
            Connect to Database
          </h1>
          <p className="text-gray-400">Enter your database connection details below</p>
        </div>

        <div className="card-gradient p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Database Type */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Database Type
              </label>
              <div className="grid grid-cols-3 gap-3">
                {dbTypes.map((db) => (
                  <button
                    key={db.value}
                    type="button"
                    onClick={() => handleInputChange({ target: { name: 'type', value: db.value } })}
                    className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                      formData.type === db.value
                        ? 'border-violet-500 bg-violet-900/30'
                        : 'border-gray-700 hover:border-gray-600'
                    }`}
                  >
                    <div className="text-3xl mb-1">{db.icon}</div>
                    <div className="text-sm font-medium">{db.label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Connection Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Host
                </label>
                <input
                  type="text"
                  name="host"
                  value={formData.host}
                  onChange={handleInputChange}
                  required
                  placeholder="localhost or example.com"
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent text-white placeholder-gray-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Port
                </label>
                <input
                  type="text"
                  name="port"
                  value={formData.port}
                  onChange={handleInputChange}
                  required
                  placeholder={selectedDbType?.defaultPort}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent text-white placeholder-gray-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Database Name
              </label>
              <input
                type="text"
                name="database"
                value={formData.database}
                onChange={handleInputChange}
                required
                placeholder="my_database"
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent text-white placeholder-gray-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Username
                </label>
                <input
                  type="text"
                  name="user"
                  value={formData.user}
                  onChange={handleInputChange}
                  required
                  placeholder="database_user"
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent text-white placeholder-gray-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  placeholder="••••••••"
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent text-white placeholder-gray-500"
                />
              </div>
            </div>

            {error && (
              <div className="p-4 bg-red-900/30 border border-red-700 rounded-lg text-red-300 text-sm">
                {error}
              </div>
            )}

            {success && (
              <div className="p-4 bg-green-900/30 border border-green-700 rounded-lg text-green-300 text-sm">
                Database connected successfully! Redirecting...
              </div>
            )}

            <div className="flex space-x-4">
              <button
                type="submit"
                disabled={loading}
                className={`flex-1 py-3 px-6 rounded-lg font-medium transition-all duration-200 ${
                  loading
                    ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl'
                }`}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Connecting...
                  </span>
                ) : (
                  'Connect'
                )}
              </button>
              
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="px-6 py-3 border-2 border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 transition-all duration-200 font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
        
        {/* Help Section */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Need help? Check out our supported databases:</p>
          <div className="mt-2 flex justify-center space-x-4">
            <span className="text-violet-400">PostgreSQL</span>
            <span>•</span>
            <span className="text-violet-400">MySQL</span>
            <span>•</span>
            <span className="text-violet-400">MS SQL Server</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatabaseConnect;